import React, { useEffect, useRef, useState } from 'react'
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrow-down.svg'
import { useDispatch } from 'react-redux'
import { closeLoader, openLoader, useLoader } from '../../redux_toolkit/Loader'
import { getUserPreference, isPageReachBottom } from '../../helpers/Util'
import { useFilter } from '../../redux_toolkit/V3/Filter'
import {
    fetchFavouriteFolderLists,
    removeFolder
} from '../../services/v3/Favourite/FavouriteService'
import FolderCardView from './FolderCardView'
import { setSystemUserPreferences } from '../../services/CommonService'
import AddFolderModal from '../common/AddFolderModal'
import CustomDropdown from '../common/CustomDropdown'
import { FavouriteThunks } from '../../redux_toolkit/Favourite/FavouriteThunks'
import { GET_FAVOURITE_COUNT } from '../../redux_toolkit/@types/thunk.types'
import {
    FAVOURITE_SORT_OPTIONS,
    FAVOURITE_TAB_CONFIG
} from './helper/favouriteUtils'
import { useNavigate, useLocation } from 'react-router-dom'

const FavouriteList = () => {
    const [isShowCreateModal, setIsShowCreateModal] = useState(false)
    const [showOptions, setShowOptions] = useState(false)
    const [selectedId, setSelectedId] = useState(null)
    const [favouriteList, setFavouriteList] = useState([])
    const [sort, setSort] = useState(FAVOURITE_SORT_OPTIONS[0])
    const location = useLocation()
    const navigate = useNavigate()
    const activeParam = new URLSearchParams(location.search).get('active')
    const [activeTab, setActiveTab] = useState(
        activeParam && FAVOURITE_TAB_CONFIG[activeParam]
            ? activeParam
            : FAVOURITE_TAB_CONFIG.ALL.id
    )
    const scrollRef = useRef()
    const dispatch = useDispatch()
    const globalLoader = useLoader()
    const isLoading = useRef(null)

    const setIsDataLoading = (data) => {
        isLoading.current = data
    }

    useEffect(() => {
        setIsDataLoading(globalLoader)
    }, [globalLoader])

    useEffect(() => {
        const newActiveParam = new URLSearchParams(location.search).get(
            'active'
        )
        if (
            newActiveParam &&
            FAVOURITE_TAB_CONFIG[newActiveParam] &&
            newActiveParam !== activeTab
        ) {
            setActiveTab(newActiveParam)
        }
    }, [location.search])

    const generateParams = (pageNumber) => {
        const queryParams = new URLSearchParams({
            page: pageNumber,
            size: 20,
            sortBy: sort.key
        })

        return `?${queryParams.toString()}`
    }

    const fetchFolderList = async (pageNumber) => {
        try {
            dispatch(openLoader())
            const activeTabConfig = FAVOURITE_TAB_CONFIG[activeTab]
            const requestBody = {
                organizationTypeList: activeTabConfig.organizationTypes
            }
            const data = await fetchFavouriteFolderLists(
                generateParams(pageNumber),
                requestBody
            )
            if (pageNumber === 0) {
                scrollRef.current = data
                setFavouriteList(data.data)
            } else {
                let tmpData = [...scrollRef.current.data, ...data.data]
                scrollRef.current = data
                scrollRef.current.data = tmpData
                setFavouriteList(tmpData)
            }
            dispatch(closeLoader())
        } catch (e) {
            dispatch(closeLoader())
        }
    }

    useEffect(() => {
        fetchFolderList(0)
        document.addEventListener('scroll', handleScroll)
        return () => {
            document.removeEventListener('scroll', handleScroll)
        }
    }, [sort, activeTab])

    const handleScroll = () => {
        if (!scrollRef.current) return
        if (isPageReachBottom()) {
            let { totalElements, currentPage, data } = scrollRef.current
            if (
                totalElements > 0 &&
                totalElements !== data.length &&
                !isLoading.current
            ) {
                fetchFolderList(currentPage + 1)
            }
        }
    }

    const removeItem = async (folderId) => {
        try {
            dispatch(openLoader())
            await removeFolder(folderId)
            let defaultFolderId = await getUserPreference()
                ?.defaultFavouriteFolder?.id
            if (defaultFolderId === folderId) {
                await setSystemUserPreferences({
                    defaultFavouriteFolder: {}
                })
            }
            scrollRef.current.data = scrollRef.current.data.filter(
                (item) => item.id !== folderId
            )
            setFavouriteList(scrollRef.current.data)
            await dispatch(FavouriteThunks[GET_FAVOURITE_COUNT]())
            dispatch(closeLoader())
        } catch (e) {
            dispatch(closeLoader())
        }
    }

    const renderList = () => {
        return favouriteList?.map((item, i) => {
            return (
                <React.Fragment key={`favourites_${item.name}_${item.id}`}>
                    <FolderCardView
                        folderInfo={item}
                        removeItem={removeItem}
                        setSelectedId={setSelectedId}
                    />
                </React.Fragment>
            )
        })
    }

    const getFilteredSortOptions = () => {
        if (activeTab === FAVOURITE_TAB_CONFIG.NITEX.id) {
            return FAVOURITE_SORT_OPTIONS.filter(
                (option) => option.key !== 'MINE' && option.key !== 'SHARED'
            )
        }
        return FAVOURITE_SORT_OPTIONS
    }

    const onClickSort = (option) => {
        const filteredOptions = getFilteredSortOptions()
        if (!filteredOptions.find((opt) => opt.key === sort.key)) {
            setSort(filteredOptions[0])
        }
        setSort(option)
        setShowOptions(false)
    }

    const handleTabClick = (tabId) => {
        if (tabId !== activeTab) {
            setActiveTab(tabId)
            navigate(`/favourites?active=${tabId}`, { replace: true })
        }
    }

    const renderTabs = () => {
        return Object.values(FAVOURITE_TAB_CONFIG).map((tab) => (
            <li key={tab.id} className={activeTab === tab.id ? 'active' : ''}>
                <span
                    className='task-name regular-14 cursor-pointer'
                    onClick={() => handleTabClick(tab.id)}
                >
                    {tab.label}
                </span>
            </li>
        ))
    }

    return (
        <>
            <div className='body-content'>
                <div className='d-flex justify-between items-center py-4'>
                    <ul className='common-tab on-favourite-list'>
                        {renderTabs()}
                    </ul>

                    <div className='flex items-center gap-x-4'>
                        {activeTab !== 'NITEX' && (
                            <button
                                className='button primary'
                                onClick={() => setIsShowCreateModal(true)}
                            >
                                Create new folder
                            </button>
                        )}
                        <CustomDropdown
                            label={'Sort'}
                            icon={<ArrowDownIcon />}
                            selectedOption={sort.value}
                            isShowOptions={showOptions}
                            setIsShowOptions={setShowOptions}
                        >
                            <div className='list-items scroll-y-label cursor-pointer'>
                                {getFilteredSortOptions()?.map((item) => {
                                    return (
                                        <div
                                            key={`sort_${item.value}`}
                                            className='single-item'
                                            onClick={() => {
                                                onClickSort(item)
                                            }}
                                        >
                                            <p>{item.value}</p>
                                        </div>
                                    )
                                })}
                            </div>
                        </CustomDropdown>
                    </div>
                </div>
                <div />
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-6 mb-4'>
                    {renderList()}
                </div>
            </div>

            {(isShowCreateModal || selectedId) && (
                <AddFolderModal
                    isShowModal={isShowCreateModal}
                    closeModal={() => {
                        setIsShowCreateModal(false)
                        setSelectedId(null)
                    }}
                    fetchFolderList={() => fetchFolderList(0)}
                    folderId={selectedId}
                />
            )}
        </>
    )
}

export default FavouriteList
